import React, { useMemo } from "react";
import parse from "html-react-parser";
import DOMPurify from "dompurify";

/**
 * Safely renders a CMS section's `content` string. TipTap saves rich-text
 * as HTML strings (e.g. "<p>Hello <strong>world</strong></p>"); older or
 * migration-seeded content is plain text — both are handled here.
 *
 * - DOMPurify sanitises to a strict allow-list of tags + attrs (no <script>,
 *   no event handlers, no unknown attrs).
 * - html-react-parser converts the sanitised markup to real React elements
 *   (no dangerouslySetInnerHTML).
 * - Plain text passes through untouched.
 */
const ALLOWED_TAGS = [
  "p", "br", "strong", "b", "em", "i", "u", "s", "blockquote",
  "ul", "ol", "li", "a", "h1", "h2", "h3", "h4", "span",
];
const ALLOWED_ATTR = ["href", "target", "rel", "class"];

function looksLikeHtml(str) {
  return typeof str === "string" && /<[a-zA-Z][^>]*>/.test(str);
}

export default function RichContent({ html, className, as: Tag = "div" }) {
  const parsed = useMemo(() => {
    if (html == null) return null;
    const str = typeof html === "string" ? html : String(html);
    if (!str.trim()) return null;
    if (!looksLikeHtml(str)) return str;
    const clean = DOMPurify.sanitize(str, {
      ALLOWED_TAGS,
      ALLOWED_ATTR,
      // Auto-add rel="noopener" on <a target="_blank"> for safety.
      ADD_ATTR: ["target"],
    });
    return parse(clean);
  }, [html]);

  if (parsed == null) return null;
  return <Tag className={className}>{parsed}</Tag>;
}
